import { IAvailability, IServiceInfo, IStop } from '../models'

/**
 * Stops carry the way the bus runs through them: 0 (forward) is the outbound trip of a round trip,
 * 1 (backwards) the return. Stops with no direction serve both.
 */
export const FORWARD_DIRECTION = 0
export const BACKWARD_DIRECTION = 1

function hasDirection(direction?: number | null): direction is number {
  return direction === FORWARD_DIRECTION || direction === BACKWARD_DIRECTION
}

function samePlace(first: IStop, second: IStop): boolean {
  const firstCoordinates = first.location?.geometry?.coordinates
  const secondCoordinates = second.location?.geometry?.coordinates
  if (!firstCoordinates || !secondCoordinates) return false
  return firstCoordinates.join(',') === secondCoordinates.join(',')
}

/** The direction of a stop, taken from the service when the stop itself does not carry it. */
export function stopDirection(stop?: IStop | null, serviceInfo?: IServiceInfo): number | undefined {
  if (!stop) return undefined
  if (hasDirection(stop.direction)) return stop.direction
  const serviceStop = serviceInfo?.stops?.find((candidate) => candidate.id === stop.id)
  return hasDirection(serviceStop?.direction) ? serviceStop!.direction : undefined
}

/** The direction of a trip: the one of its stops. */
export function tripDirection(
  beginningStop?: IStop | null,
  exitStop?: IStop | null,
  serviceInfo?: IServiceInfo
): number | undefined {
  return stopDirection(beginningStop, serviceInfo) ?? stopDirection(exitStop, serviceInfo)
}

export function availabilityDirection(availability?: IAvailability): number | undefined {
  return tripDirection(availability?.inStop, availability?.outStops?.[0])
}

/** The same place as the stop, but served in the given direction. */
export function stopInDirection(stop: IStop, direction: number, serviceInfo?: IServiceInfo): IStop {
  const current = stopDirection(stop, serviceInfo)
  if (!serviceInfo?.stops || current === undefined || current === direction) return stop
  const serviceStop = serviceInfo.stops.find((candidate) => candidate.id === stop.id) ?? stop
  return serviceInfo.stops.find((candidate) => candidate.direction === direction && samePlace(candidate, serviceStop)) ?? stop
}

/** The turning point of a round trip line: the last stop of the outbound and the first of the return. */
export function hasInflexion(serviceInfo?: IServiceInfo): boolean {
  return !!serviceInfo?.stops?.some((stop) => stop.inflexion)
}

function isInflexion(stop: IStop, serviceInfo?: IServiceInfo): boolean {
  const serviceStop = serviceInfo?.stops?.find((candidate) => candidate.id === stop.id) ?? stop
  // Only the return copy of the stop is flagged; the outbound one is the same place.
  return !!serviceInfo?.stops?.some((candidate) =>
    candidate.inflexion && (candidate.id === serviceStop.id || samePlace(candidate, serviceStop))
  )
}

/**
 * A single stop round trip is booked from one stop, and the other one is always the inflexion: the
 * last stop of the outbound, the first one of the return. So the user's stop is the one that is
 * not the inflexion, the origin of an outbound trip and the destination of a return. Without an
 * inflexion to tell, the direction of the trip does.
 */
export function singleStopPick(
  beginningStop: IStop,
  exitStop: IStop,
  serviceInfo?: IServiceInfo
): { stop: IStop, isOrigin: boolean } {
  if (isInflexion(exitStop, serviceInfo)) return { stop: beginningStop, isOrigin: true }
  if (isInflexion(beginningStop, serviceInfo)) return { stop: exitStop, isOrigin: false }
  return tripDirection(beginningStop, exitStop, serviceInfo) === BACKWARD_DIRECTION
    ? { stop: exitStop, isOrigin: false }
    : { stop: beginningStop, isOrigin: true }
}
